/**
 * Card 2508 (contract A6) — which page a board address renders.
 *
 * <p>`/onboarding` is the tenant's DEFAULT board and `/boards/<workflow_id>` a workflow's own. Both
 * come here. The board's first read (every file, no view) says whether its declaration names
 * VIEWS: if it does the board is drawn as tiles and tabs ({@link ViewsBoardPage}); if not it is the
 * attention-first files board ({@link FilesBoardPage}), exactly as before. The files board makes the
 * same first read under the same cache key, so deciding costs no extra request.</p>
 *
 * <p>Every refusal (a workflow the tenant has no board for, a failed read) and the loading state
 * are the files board's to word: this switch only picks the views page when there is a views answer
 * to draw.</p>
 */
import { useParams } from "react-router-dom";
import { useClientTenant, useFamilies, useJobStatusFiles } from "../api/portalQueries.js";
import { FilesBoardPage, resolveBoard } from "./FilesBoardPage.js";
import { ViewsBoardPage } from "./ViewsBoardPage.js";

export function BoardSwitch({ workflowId }: { workflowId?: string }): JSX.Element {
  const identity = useClientTenant();
  const families = useFamilies(identity.tenantId);
  const board = resolveBoard(families.data?.families, workflowId);
  const overview = useJobStatusFiles(identity.tenantId, {
    workflowId: board?.workflowId ?? null,
    enabled: board !== undefined,
  });

  const views = overview.data?.views ?? [];
  if (board !== undefined && overview.data !== undefined && views.length > 0) {
    return (
      <ViewsBoardPage
        board={board}
        overview={overview.data}
        refreshOverview={() => void overview.refetch()}
      />
    );
  }

  // Until there is a views answer — loading, refused, failed or simply no views — the files board
  // is what renders: it words every one of those states, and it stays MOUNTED through them (a
  // switch that swapped in its own skeleton would unmount it and remount it on every refetch).
  return <FilesBoardPage workflowId={workflowId} />;
}

/** `/boards/<workflow_id>`. */
export function WorkflowBoardRoute(): JSX.Element {
  const { workflowId } = useParams<{ workflowId: string }>();
  return <BoardSwitch workflowId={workflowId ?? ""} />;
}

export default WorkflowBoardRoute;
